'use client';
import { S8StockRecovery } from '@/components/medications/S8StockRecovery';
import { clearS8StockHandles } from '@/lib/medications/s8-stock-client';
import { clearUnifiedCaptureRetries } from '@/lib/incidents/unified-capture';
import {
  clearImprovementDrafts,
  installImprovementDraftSignOutCleanup,
} from '@/lib/compliance/improvement-drafts';
import {
  clearWhsDrafts,
  installWhsDraftSignOutCleanup,
  installWhsDraftExpirySweep,
} from '@/lib/compliance/whs-drafts';
import {
  draftScope,
  installFormDraftSignOutCleanup,
  pruneForeignFormDrafts,
} from '@/hooks/form-draft-storage';

import { PageTitle } from '@statura/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { pageLabelFromPathname } from '@/lib/page-title';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryAuthBoundary } from '../../providers/query-provider';
import { queryAuthIdentityChanged } from '../../providers/query-auth-identity';
import { createClient } from '../lib/supabase/client';
import { getAuthUserWithRetry, isConfirmedAuthSignOut } from '../lib/supabase/auth-user';
import { useAuthStore } from '../stores/auth-store';
import { syncUserMetadata } from '../app/(auth)/login/sync-metadata';
import { resolveAuthProfile, verifyAuthProfileCommit } from '@/lib/auth/resolve-auth-profile';
import { reportClientAuthDiagnostic } from '../lib/auth/auth-diagnostics';
import { AppLoadingState } from './app-loading-state';
import { ResolvedAuthUserContext } from '@/contexts/resolved-auth-user-context';
import type { AuthUserProfile } from '@/stores/auth-store';
import {
  admitServerAuthSeed,
  isSessionEventAuthHint,
  isSupersededAuthResolution,
} from '@/lib/auth/server-seed-admission';
import {
  MAX_LOGIN_BOUNCES,
  clearLocalAuthSession,
  clearLoginBounces,
  readLoginBounceCount,
  recordLoginBounce,
  recoverAuthActorViaRefresh,
  type AuthActorResolution,
} from './auth-guard-state';

const AUTH_USER_TIMEOUT_MS = 5_000;
const POST_LOGIN_WORKSPACE_PROBE_KEY = 'statura.auth.postLoginWorkspaceProbe';
const POST_LOGIN_WORKSPACE_PROBE_MAX_AGE_MS = 60_000;
const POST_LOGIN_WORKSPACE_RELOAD_KEY = 'statura.auth.postLoginWorkspaceReloadAttempted';
const POST_LOGIN_WORKSPACE_RECOVERY_MS = 10_000;

async function resolveInitialAuthUser(
  supabase: ReturnType<typeof createClient>,
  isCurrent: () => boolean,
): Promise<AuthActorResolution> {
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async (): Promise<AuthActorResolution> => {
        const { data, error } = await getAuthUserWithRetry(supabase, { retries: 1, delayMs: 100 });
        if (data.user) return { kind: 'authenticated', user: data.user };
        if (isConfirmedAuthSignOut(error)) return { kind: 'signed-out' };
        // Refresh can recover a stale token, but never use locally decoded session claims.
        return recoverAuthActorViaRefresh(supabase, () => !timedOut && isCurrent());
      })().catch((): AuthActorResolution => ({ kind: 'unavailable' })),
      new Promise<AuthActorResolution>((resolve) => {
        timer = setTimeout(() => {
          timedOut = true;
          resolve({ kind: 'unavailable' });
        }, AUTH_USER_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function consumePostLoginWorkspaceProbe() {
  try {
    const raw = window.sessionStorage.getItem(POST_LOGIN_WORKSPACE_PROBE_KEY);
    if (!raw) return false;
    window.sessionStorage.removeItem(POST_LOGIN_WORKSPACE_PROBE_KEY);
    const recordedAt = Number.parseInt(raw, 10);
    return (
      Number.isFinite(recordedAt) &&
      Date.now() - recordedAt <= POST_LOGIN_WORKSPACE_PROBE_MAX_AGE_MS
    );
  } catch {
    return false;
  }
}

function reportPostLoginWorkspaceOpened() {
  if (!consumePostLoginWorkspaceProbe()) return;
  reportClientAuthDiagnostic('login_workspace_opened', {
    reason: 'post_login_profile_resolved',
  });
}

export function AuthGuard({
  children,
  initialUser = null,
}: {
  children: React.ReactNode;
  initialUser?: AuthUserProfile | null;
}) {
  useEffect(installWhsDraftSignOutCleanup, []);
  useEffect(installFormDraftSignOutCleanup, []);
  useEffect(installImprovementDraftSignOutCleanup, []);
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const authBoundary = useQueryAuthBoundary();
  const user = useAuthStore((s) => s.user);
  useEffect(() => {
    if (user?.id) return installWhsDraftExpirySweep();
  }, [user?.id]);
  // A new identity in a retained tab (e.g. after idle logout) never inherits
  // another operator's form drafts, even if it never opens that form.
  const formDraftScope = draftScope(user);
  useEffect(() => {
    if (!formDraftScope) return;
    try {
      pruneForeignFormDrafts(window.sessionStorage, formDraftScope);
    } catch {
      /* Storage denial must not block the app shell. */
    }
  }, [formDraftScope]);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const admittedInitialUser = useMemo(
    () => admitServerAuthSeed(initialUser, user, authBoundary.getLastCommittedIdentity()),
    [initialUser, user, authBoundary],
  );
  authBoundary.publishAdmittedServerSeed(admittedInitialUser);
  // The dashboard server layout has already verified the session and resolved
  // this profile. Reuse that proof for the first paint while the browser
  // independently revalidates; do not replace production content with a
  // client-only auth loader on every hard navigation. Stale server props from a
  // prior navigation are not re-admitted once the store holds a different actor.
  const [initialUserActive, setInitialUserActive] = useState(() => Boolean(admittedInitialUser));
  const activeInitialUserRef = useRef(admittedInitialUser);
  activeInitialUserRef.current = initialUserActive ? admittedInitialUser : null;
  const [identityState, setIdentityState] = useState<
    'ready' | 'checking' | 'unavailable' | 'inactive' | 'missing' | 'missing-role'
  >('ready');
  const [retryVersion, setRetryVersion] = useState(0);
  const lastVisibilityAtRef = useRef<number | null>(null);
  const lastRetryAtRef = useRef<number | null>(null);
  const [retryThrottled, setRetryThrottled] = useState(false);
  const verifiedIdentityRef = useRef<AuthUserProfile | null>(
    user &&
      authBoundary.getLastVerifiedIdentity()?.userId === user.id &&
      authBoundary.getLastVerifiedIdentity()?.tenantId === user.tenantId &&
      authBoundary.getLastVerifiedIdentity()?.role === user.role
      ? user
      : null,
  );
  const failuresRef = useRef<{ count: number; firstAt: number } | null>(null);
  const resolvedUser =
    identityState === 'ready'
      ? initialUserActive && admittedInitialUser
        ? admittedInitialUser
        : user
      : null;
  const [isLoading, setIsLoading] = useState(() => !(admittedInitialUser ?? user));
  // Tripped when the client keeps resolving "no user" while the server keeps
  // treating the session as valid — stops the /login bounce loop dead instead
  // of redirecting again (2026-06-09).
  const [loopBroken, setLoopBroken] = useState(false);
  const [isRecoveringSession, setIsRecoveringSession] = useState(false);
  const loginHref = useMemo(() => {
    if (!pathname || pathname === '/login') return '/login';
    return `/login?next=${encodeURIComponent(pathname)}`;
  }, [pathname]);
  // Never build user-facing copy out of raw path segments: that renders route
  // internals, and on a detail route it renders the record's UUID
  // ("Opening roster / periods / 882e96eb 09d7 ..."). pageLabelFromPathname
  // carries the real labels and drops or names dynamic segments.
  const destinationLabel = useMemo(() => {
    if (!pathname || pathname === '/') return 'your dashboard';
    return pageLabelFromPathname(pathname);
  }, [pathname]);

  useEffect(() => {
    if (!admittedInitialUser) {
      setInitialUserActive(false);
    }
  }, [admittedInitialUser]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let probeRaw: string | null = null;
    try {
      probeRaw = window.sessionStorage.getItem(POST_LOGIN_WORKSPACE_PROBE_KEY);
    } catch {
      return;
    }
    if (!probeRaw) return;

    const recordedAt = Number.parseInt(probeRaw, 10);
    if (
      !Number.isFinite(recordedAt) ||
      Date.now() - recordedAt > POST_LOGIN_WORKSPACE_PROBE_MAX_AGE_MS
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      try {
        // Workspace opened — probe was consumed by reportPostLoginWorkspaceOpened.
        if (!window.sessionStorage.getItem(POST_LOGIN_WORKSPACE_PROBE_KEY)) return;
        if (window.sessionStorage.getItem(POST_LOGIN_WORKSPACE_RELOAD_KEY)) return;

        window.sessionStorage.setItem(POST_LOGIN_WORKSPACE_RELOAD_KEY, Date.now().toString());
        reportClientAuthDiagnostic('login_workspace_recovery_reload', {
          reason: 'post_login_probe_timeout',
        });
        window.location.reload();
      } catch {
        // Best-effort recovery only.
      }
    }, POST_LOGIN_WORKSPACE_RECOVERY_MS);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;
    let authStateChangeVersion = 0;
    let resolving = false;

    const applyVerifiedClear = () => {
      const previous = useAuthStore.getState().user;
      activeInitialUserRef.current = null;
      verifiedIdentityRef.current = null;
      failuresRef.current = null;
      clearAuth();
      authBoundary.notifyVerifiedIdentityWrite(previous, null);
      setInitialUserActive(false);
    };

    const resolveAndCommit = async (
      resolveActor: (isCurrent: () => boolean) => Promise<AuthActorResolution>,
    ) => {
      const version = ++authStateChangeVersion;
      const generation = authBoundary.captureVerifiedWriteGeneration();
      const identityAtStart = useAuthStore.getState().user;
      const isCurrent = () =>
        !cancelled &&
        version === authStateChangeVersion &&
        !isSupersededAuthResolution(generation, authBoundary.captureVerifiedWriteGeneration()) &&
        !queryAuthIdentityChanged(identityAtStart, useAuthStore.getState().user);
      const applyUnavailable = () => {
        const verified = verifiedIdentityRef.current;
        const previousFailures = failuresRef.current;
        const failures = {
          count: (previousFailures?.count ?? 0) + 1,
          firstAt: previousFailures?.firstAt ?? Date.now(),
        };
        failuresRef.current = failures;
        const retain =
          verified &&
          !queryAuthIdentityChanged(verified, identityAtStart) &&
          !(failures.count >= 4 && Date.now() - failures.firstAt >= 15 * 60 * 1000);
        reportClientAuthDiagnostic('identity_revalidation_unavailable', {
          reason: retain ? 'verified_identity_retained' : 'admission_unavailable',
        });
        if (retain) return;
        applyVerifiedClear();
        void queryClient.cancelQueries();
        setIdentityState('unavailable');
      };
      resolving = true;
      let applied = false;
      try {
        const actorResult = await resolveActor(isCurrent);
        if (!isCurrent()) return;
        if (actorResult.kind !== 'authenticated') {
          applied = true;
          if (!verifiedIdentityRef.current) {
            reportClientAuthDiagnostic('initial_user_missing', {
              reason: 'initial_resolution_null',
            });
          }
          if (actorResult.kind === 'signed-out') {
            reportClientAuthDiagnostic('auth_state_signed_out', { reason: 'confirmed_no_session' });
            applyVerifiedClear();
            setIdentityState('ready');
          } else applyUnavailable();
          return;
        }
        const actor = actorResult.user;
        if (identityAtStart && actor.id !== identityAtStart.id) {
          // The verified browser principal changed before its cross-tab event arrived.
          applied = true;
          applyVerifiedClear();
          setIdentityState('checking');
          void resolveAndCommit((isCurrent) => resolveInitialAuthUser(supabase, isCurrent));
          return;
        }
        const resolution = await resolveAuthProfile(supabase, actor, syncUserMetadata);
        const result = await verifyAuthProfileCommit(supabase, actor.id, resolution, isCurrent);
        if (!result || !isCurrent()) return;
        applied = true;
        if (result.kind === 'resolved') {
          // No await between the final generation/event/principal checks and this write.
          const previous = useAuthStore.getState().user;
          setAuth(result.profile);
          authBoundary.notifyVerifiedIdentityWrite(previous, result.profile);
          verifiedIdentityRef.current = result.profile;
          failuresRef.current = null;
          setInitialUserActive(false);
          setIdentityState('ready');
          reportPostLoginWorkspaceOpened();
        } else if (result.kind === 'unavailable') {
          applyUnavailable();
        } else {
          applyVerifiedClear();
          void queryClient.cancelQueries();
          if (result.kind === 'blocked') {
            reportClientAuthDiagnostic('identity_revalidation_blocked', { reason: result.reason });
            setIdentityState(result.reason);
          } else if (result.kind === 'signed-out') {
            clearWhsDrafts();
            clearImprovementDrafts();
            try {
              clearUnifiedCaptureRetries(window.sessionStorage);
            } catch {
              /* Storage denial must not prevent sign-out. */
            }
            reportClientAuthDiagnostic('auth_state_signed_out', { reason: 'confirmed_no_session' });
            setIdentityState('ready');
          } else {
            setIdentityState('unavailable');
          }
        }
      } catch {
        if (!isCurrent()) return;
        applied = true;
        applyUnavailable();
      } finally {
        if (!cancelled && version === authStateChangeVersion) {
          resolving = false;
          if (!applied && !isCurrent()) {
            // A newer verified store identity may supersede this request without an auth event.
            setInitialUserActive(false);
            setIdentityState(
              authBoundary.captureVerifiedWriteGeneration() !== generation &&
                useAuthStore.getState().user
                ? 'ready'
                : 'unavailable',
            );
          }
          setIsLoading(false);
          setIsRecoveringSession(false);
        }
      }
    };

    // Preserve server/store first paint, but independently verify the browser actor.
    void resolveAndCommit((isCurrent) => resolveInitialAuthUser(supabase, isCurrent));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // INITIAL_SESSION is only a local hint; the independent initial getUser owns admission.
      if (isSessionEventAuthHint(event)) return;
      if (event === 'SIGNED_OUT') {
        clearS8StockHandles();
        clearWhsDrafts();
        clearImprovementDrafts();
        try {
          clearUnifiedCaptureRetries(window.sessionStorage);
        } catch {
          /* Storage denial must not prevent sign-out. */
        }
        reportClientAuthDiagnostic('auth_state_signed_out', { reason: 'SIGNED_OUT' });
        ++authStateChangeVersion;
        resolving = false;
        applyVerifiedClear();
        setIdentityState('ready');
        setIsLoading(false);
        setIsRecoveringSession(false);
        return;
      }
      const current = useAuthStore.getState().user;
      // Same-actor refresh/focus/update events revalidate without unmounting the workspace.
      // A null session hint cannot prove a sign-out; getUser owns that decision.
      if (session?.user && current && session.user.id !== current.id) {
        applyVerifiedClear();
        setIdentityState('checking');
      } else if (
        !current &&
        !(session?.user && activeInitialUserRef.current?.id === session.user.id)
      ) {
        setIdentityState('checking');
      }
      // On first hydration the verified server actor can precede the store.
      // A matching event keeps that already-admitted paint while revalidating;
      // it does not authorise an actor, populate the store or revive a cleared seed.
      // Leave the Supabase auth callback/lock before asking Auth for verified identity.
      void resolveAndCommit(async (isCurrent) => {
        await Promise.resolve();
        return resolveInitialAuthUser(supabase, isCurrent);
      });
    });

    // Revalidate the whole canonical identity, including ACTIVE status and tenant.
    const roleCheckInterval = setInterval(
      () => {
        if (cancelled || resolving) return;
        void resolveAndCommit((isCurrent) => resolveInitialAuthUser(supabase, isCurrent));
      },
      5 * 60 * 1000,
    );

    const onVisible = () => {
      if (document.visibilityState !== 'visible' || cancelled || resolving) return;
      if (lastVisibilityAtRef.current !== null && Date.now() - lastVisibilityAtRef.current < 30_000)
        return;
      lastVisibilityAtRef.current = Date.now();
      setIsRecoveringSession(true);
      reportClientAuthDiagnostic('visibility_recovery_start');
      // The initial verifier already refreshes a stale token when needed.
      void resolveAndCommit((isCurrent) => resolveInitialAuthUser(supabase, isCurrent));
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearInterval(roleCheckInterval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [setAuth, clearAuth, authBoundary, queryClient, retryVersion]);

  // A validated session clears the loop guard so a later, genuine sign-out can bounce normally.
  useEffect(() => {
    if (resolvedUser) clearLoginBounces();
  }, [resolvedUser]);

  useEffect(() => {
    if (identityState !== 'ready') return;
    if (isLoading || resolvedUser || loopBroken || isRecoveringSession) return;

    // Abandoning the workspace: in-flight tenant-scoped queries would otherwise
    // keep fetching without a session, fail as anon ("permission denied for
    // table …", "No tenant associated…"), and land phantom Sentry errors tagged
    // /login. Cancellation is not an error in react-query, so no capture fires;
    // it also doesn't wipe cached data (unlike clear() — see query-provider's
    // auth identity boundary for why a wipe here would be wrong).
    void queryClient.cancelQueries();

    // We have no validated user and we're done loading, so we want the /login
    // form. But the SERVER may still hold a valid session (the in-memory access
    // token went stale faster than the still-valid refresh-token cookie). In
    // that case /login redirects us straight back here — an infinite bounce
    // loop plus a GET /user storm that freezes the tab. Cap the bounces: after
    // MAX_LOGIN_BOUNCES, stop redirecting and surface a recoverable "out of
    // sync" state instead. The happy path (silent refresh in
    // resolveInitialAuthUser) never reaches this effect; a genuine logout
    // produces a single bounce and lands on the /login form.
    if (readLoginBounceCount() >= MAX_LOGIN_BOUNCES) {
      reportClientAuthDiagnostic('login_bounce_loop_broken');
      setLoopBroken(true);
      return;
    }
    recordLoginBounce();
    reportClientAuthDiagnostic('login_bounce_recorded');
    router.replace(loginHref);
  }, [
    identityState,
    isLoading,
    resolvedUser,
    loopBroken,
    isRecoveringSession,
    router,
    loginHref,
    queryClient,
  ]);

  if (
    identityState === 'unavailable' ||
    identityState === 'inactive' ||
    identityState === 'missing' ||
    identityState === 'missing-role'
  ) {
    const blocked = identityState !== 'unavailable';
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface-shell px-4"
        role="status"
        aria-live="polite"
      >
        <div className="w-full max-w-md space-y-5 rounded-card border border-border bg-card p-8 text-center shadow-card">
          <PageTitle className="font-heading text-section-title text-statura-ink">
            {blocked ? 'Workspace access unavailable' : 'Checking workspace access'}
          </PageTitle>
          <p className="text-body text-text-muted">
            {identityState === 'inactive'
              ? 'Your account has been deactivated. Contact your organisation administrator.'
              : identityState === 'missing'
                ? 'Your account is not linked to an organisation. Contact your organisation administrator.'
                : identityState === 'missing-role'
                  ? 'Your account does not have a workspace role. Contact your organisation administrator.'
                  : 'We could not confirm your workspace access just now. Please try again shortly.'}
          </p>
          {blocked ? (
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-control bg-statura-green-accessible px-4 py-2 text-body font-medium text-primary-foreground transition hover:bg-statura-green-accessible-hover"
              onClick={() => {
                void clearLocalAuthSession(createClient()).finally(() => {
                  window.location.assign(
                    identityState === 'inactive' ? '/login?reason=account_deactivated' : loginHref,
                  );
                });
              }}
            >
              Go to sign in
            </button>
          ) : (
            <>
              <button
                type="button"
                className="inline-flex w-full items-center justify-center rounded-control bg-statura-green-accessible px-4 py-2 text-body font-medium text-primary-foreground transition hover:bg-statura-green-accessible-hover"
                onClick={() => {
                  if (
                    lastRetryAtRef.current !== null &&
                    Date.now() - lastRetryAtRef.current < 5_000
                  ) {
                    setRetryThrottled(true);
                    return;
                  }
                  lastRetryAtRef.current = Date.now();
                  setRetryThrottled(false);
                  setIdentityState('checking');
                  setIsLoading(true);
                  setRetryVersion((version) => version + 1);
                }}
              >
                Try again
              </button>
              {retryThrottled && (
                <p className="text-body text-text-muted">
                  Please wait a few seconds before trying again.
                </p>
              )}
              <button
                type="button"
                className="text-body font-medium text-statura-ink underline"
                onClick={() => {
                  void clearLocalAuthSession(createClient()).finally(() =>
                    window.location.assign(loginHref),
                  );
                }}
              >
                Sign out of this browser
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isLoading || identityState === 'checking') {
    return (
      <AppLoadingState
        title={`Opening ${destinationLabel}`}
        description="Checking your session and role access before loading this workspace."
      />
    );
  }

  if (isRecoveringSession && !resolvedUser) {
    return (
      <AppLoadingState
        title={`Reconnecting ${destinationLabel}`}
        description="Refreshing your secure session before opening this workspace."
      />
    );
  }

  // Loop breaker tripped: the client could not reconcile with the server after
  // several attempts. Show a terminal, recoverable state instead of bouncing to
  // /login again. Both actions first drop the stale local session (the usual
  // cause of the loop) so the subsequent reload/redirect starts from a clean
  // slate — the same recovery an incognito window gets for free.
  if (loopBroken) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface-shell px-4"
        role="alert"
      >
        <div className="w-full max-w-md space-y-5 rounded-card border border-border bg-card p-8 text-center shadow-card">
          <PageTitle className="font-heading text-section-title text-statura-ink">
            Session out of sync
          </PageTitle>
          <p className="text-body text-text-muted">
            Your session is out of sync with the server. Reload to clear the stale session and
            reconnect — you&apos;ll be returned to where you left off, though you may need to sign
            in again.
          </p>
          <button
            type="button"
            onClick={() => {
              clearLoginBounces();
              // The loop is usually a stale/mismatched auth cookie the client
              // can't reconcile. A plain reload re-reads the same bad cookie and
              // re-traps the user, so drop the local session first (incognito-
              // equivalent), then reload onto a clean slate.
              const supabase = createClient();
              void clearLocalAuthSession(supabase).finally(() => {
                if (typeof window !== 'undefined') {
                  window.location.reload();
                }
              });
            }}
            className="inline-flex w-full items-center justify-center rounded-control bg-statura-green-accessible px-4 py-2 text-body font-medium text-primary-foreground transition hover:bg-statura-green-accessible-hover"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={() => {
              clearLoginBounces();
              // Clear the stale local session before navigating so /login starts
              // from a clean slate instead of bouncing back on the same cookie.
              const supabase = createClient();
              void clearLocalAuthSession(supabase).finally(() => {
                if (typeof window !== 'undefined') {
                  // Hard navigation (not router.replace) so we don't re-enter the
                  // client bounce loop. With the local session cleared the user
                  // lands on the sign-in form.
                  window.location.assign(loginHref);
                }
              });
            }}
            className="text-body font-medium text-muted-foreground underline-offset-2 transition hover:text-statura-ink hover:underline"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  // Redirect unauthenticated users to login
  if (!resolvedUser) {
    return (
      <AppLoadingState
        title="Opening sign in"
        description="Your dashboard session is not active. Redirecting to the sign-in page."
        action={
          <Link
            href={loginHref}
            className="inline-flex items-center justify-center rounded-lg bg-statura-green-accessible px-4 py-2 text-body font-medium text-primary-foreground transition hover:bg-statura-green-accessible-hover"
          >
            Go to login
          </Link>
        }
      />
    );
  }

  return (
    <ResolvedAuthUserContext.Provider value={resolvedUser}>
      <S8StockRecovery />
      {children}
    </ResolvedAuthUserContext.Provider>
  );
}
